import React, { useState } from 'react';
import { useSEO } from '@/hooks/useSEO';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { PageHero } from '@/pages/About';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const HERO = 'https://images.unsplash.com/photo-1667238002143-b5e117168e98?w=2000&q=80&auto=format&fit=crop';

const CONTACT = {
  phone: '+370 600 60388',
  email: 'groupmaisiagala@gmail.com',
};

// Google Maps embed URL - works without API key (free iframe embed)
const MAPS_EMBED = 'https://www.google.com/maps?q=Krivi%C5%B3+g.+5,+Vilnius,+Lithuania&hl=lt&z=16&output=embed';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xvznlbzo';

const Contact = () => {
  const { t } = useLanguage();
  useSEO({ title: 'Kontaktai — UAB MAIŠIAGALA GROUP', description: 'Susisiekite: +370 600 60388, groupmaisiagala@gmail.com. Vilnius, Lietuva.', path: '/contact' });
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '', honeypot: '' });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    // Honeypot — silently ignore bot submissions
    if (form.honeypot) {
      setForm({ name: '', phone: '', email: '', message: '', honeypot: '' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          message: form.message,
          _replyto: form.email,
        }),
      });
      if (!res.ok) throw new Error('Formspree submission failed');
      toast.success(t('contact.form.success'));
      setForm({ name: '', phone: '', email: '', message: '', honeypot: '' });
    } catch (err) {
      toast.error(t('contact.form.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero title={t('contact.title')} image={HERO} />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <p className="text-white/60 max-w-2xl text-lg mb-16">{t('contact.lead')}</p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 space-y-6"
            data-testid="contact-form"
          >
            {/* Honeypot for spam protection */}
            <input
              type="text"
              name="website"
              value={form.honeypot}
              onChange={update('honeypot')}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-3">
                  {t('contact.form.name')} *
                </label>
                <Input
                  required
                  data-testid="form-name"
                  value={form.name}
                  onChange={update('name')}
                  className="bg-transparent border-x-0 border-t-0 border-b border-[#262626] focus:border-gold rounded-none px-0 h-12 text-white text-base focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-3">
                  {t('contact.form.phone')} *
                </label>
                <Input
                  required
                  data-testid="form-phone"
                  value={form.phone}
                  onChange={update('phone')}
                  className="bg-transparent border-x-0 border-t-0 border-b border-[#262626] focus:border-gold rounded-none px-0 h-12 text-white text-base focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-3">
                {t('contact.form.email')} *
              </label>
              <Input
                required
                type="email"
                data-testid="form-email"
                value={form.email}
                onChange={update('email')}
                className="bg-transparent border-x-0 border-t-0 border-b border-[#262626] focus:border-gold rounded-none px-0 h-12 text-white text-base focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-3">
                {t('contact.form.message')} *
              </label>
              <Textarea
                required
                rows={5}
                data-testid="form-message"
                value={form.message}
                onChange={update('message')}
                className="bg-transparent border-x-0 border-t-0 border-b border-[#262626] focus:border-gold rounded-none px-0 text-white text-base focus-visible:ring-0 focus-visible:ring-offset-0 resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="form-submit"
              className="group inline-flex items-center gap-3 bg-gold text-black px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E6C547] transition-colors disabled:opacity-60 mt-4"
            >
              {t('contact.form.submit')}
              <Send size={14} strokeWidth={1.8} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>

          {/* Info */}
          <aside className="lg:col-span-5 space-y-10" data-testid="contact-info">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">{t('contact.info.phone')}</div>
              <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-3 text-white text-lg hover:text-gold" data-testid="contact-phone">
                <Phone size={16} strokeWidth={1.5} className="text-gold" />
                {CONTACT.phone}
              </a>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">{t('contact.info.email')}</div>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-white text-lg hover:text-gold break-all" data-testid="contact-email">
                <Mail size={16} strokeWidth={1.5} className="text-gold flex-shrink-0" />
                {CONTACT.email}
              </a>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">{t('contact.info.address')}</div>
              <div className="flex items-start gap-3 text-white text-base leading-relaxed">
                <MapPin size={16} strokeWidth={1.5} className="text-gold flex-shrink-0 mt-1" />
                <span>{t('contact.info.address_value')}</span>
              </div>
            </div>
          </aside>
        </div>

        {/* Google Maps */}
        <div className="mt-24 border border-[#262626]" data-testid="contact-map">
          <iframe
            title="UAB MAIŠIAGALA GROUP location"
            src={MAPS_EMBED}
            width="100%"
            height="450"
            style={{ border: 0, filter: 'grayscale(0.8) invert(0.92) hue-rotate(180deg)' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
};

export default Contact;
